import { Component, Output, EventEmitter, OnInit, OnDestroy, Input, ChangeDetectorRef } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NotificationsService, NotificationParams } from '@core/services/notifications/notifications.service';
import { LocalStorageService } from '@core/services/local-storage.service';
import { NotificationItem, NotificationsResponse } from '@core/interfaces/notifications';
import { GtmEventService } from '@core/analytics/gtm-events.service';

/** Estados de notificación de pedido según API. */
type OrderNotificationStatus =
  | 'REJECTED'
  | 'CREATED'
  | 'CONFIRMED'
  | 'IN_PREPARATION'
  | 'IN_TRANSIT'
  | 'DELIVERED';

@Component({
  selector: 'app-show-notifications',
  templateUrl: './show-notifications.component.html',
  styleUrls: ['./show-notifications.component.scss'],
})
export class ShowNotificationsComponent implements OnInit, OnDestroy {
  @Input() notificationCount = 0;
  /** 'cliente' = badge naranja, 'vendedor' = badge #DF1978 */
  @Input() badgeVariant: 'cliente' | 'vendedor' = 'cliente';
  /** Params explícitos para la API (sector, code). Si se pasan, se usan en lugar del contexto del servicio. */
  @Input() notificationParams?: NotificationParams;
  @Output() closeModal = new EventEmitter<void>();
  @Output() unreadCountChange = new EventEmitter<number>();

  notificationFilter: 'all' | 'pedidos' | 'promociones' | 'aliados' | 'cuenta' | 'reclamos' | 'novedades' | 'alicorp-way' | 'novedades-avisos' = 'all';
  notifications: NotificationItem[] = [];
  hasError = false;

  /** Pasos del timeline (solo notificaciones con `orderStatus`). */
  readonly orderTimelineSteps: ReadonlyArray<{ label: string }> = [
    { label: 'Creado' },
    { label: 'Confirmado' },
    { label: 'En preparación' },
    { label: 'En tránsito' },
    { label: 'Entregado' },
  ];

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly notificationsService: NotificationsService,
    private readonly localStorageService: LocalStorageService,
    private readonly cdr: ChangeDetectorRef,
    private readonly _gtmEventService: GtmEventService,
  ) {}

  ngOnInit(): void {
    this.loadNotifications();
    this.notificationsService.onPushRefresh$.pipe(takeUntil(this.destroy$)).subscribe((list) => {
      this.notifications = list;
      const unreadCount = this.notifications.filter((n) => !n.read).length;
      this.unreadCountChange.emit(unreadCount);
      this.cdr.detectChanges();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private mapResponse(res: NotificationsResponse | NotificationItem[]): NotificationItem[] {
    const list = Array.isArray(res) ? res : (res?.notifications ?? []);
    return list.map((item) => ({
      ...item,
      read: item.read ?? item.isNotified,
    }));
  }

  loadNotifications(paramsOverride?: NotificationParams): void {
    this.hasError = false;
    const params = paramsOverride ?? this.notificationParams ?? undefined;
    this.notificationsService.getNotificationsByToken(params).subscribe({
      next: (res: NotificationsResponse | NotificationItem[]) => {
        this.notifications = this.mapResponse(res);
        const unreadCount = this.notifications.filter((n) => !n.read).length;
        this.unreadCountChange.emit(unreadCount);
        this.cdr.detectChanges();
      },
      error: () => {
        this.notifications = [];
        this.hasError = true;
      },
    });
  }

  onRetry(): void {
    this.loadNotifications();
  }

  close(): void {
    this.closeModal.emit();
  }

  setFilter(filter: 'all' | 'pedidos' | 'promociones' | 'aliados' | 'cuenta' | 'reclamos' | 'novedades' | 'alicorp-way' | 'novedades-avisos'): void {
    this.notificationFilter = filter;
  }

  private readonly sectionFilterKeywordsCliente: Record<string, string> = {
    pedidos: 'pedido',
    promociones: 'promocion',
    aliados: 'aliado',
    cuenta: 'cuenta',
    reclamos: 'reclamo',
    novedades: 'novedad',
  };

  private readonly sectionFilterKeywordsVendedor: Record<string, string[]> = {
    'alicorp-way': ['alicorp', 'way'],
    promociones: ['promocion'],
    'novedades-avisos': ['novedad', 'aviso'],
  };

  private get sectionFilterKeywords(): Record<string, string | string[]> {
    return this.badgeVariant === 'vendedor' ? this.sectionFilterKeywordsVendedor : this.sectionFilterKeywordsCliente;
  }

  get filteredNotifications(): NotificationItem[] {
    if (this.notificationFilter === 'all') {
      return this.notifications;
    }
    const keywordOrKeywords = this.sectionFilterKeywords[this.notificationFilter];
    if (!keywordOrKeywords) return this.notifications;
    const keywords = Array.isArray(keywordOrKeywords) ? keywordOrKeywords : [keywordOrKeywords];
    return this.notifications.filter((item) => {
      const section = item.section?.toLowerCase() ?? '';
      return keywords.some((kw) => section.includes(kw));
    });
  }

  get badgeLabel(): string {
    return this.notificationCount > 99 ? '99+' : String(this.notificationCount);
  }

  /** Título según contexto: cliente, vendedor o vendedor con cliente seleccionado */
  get notificationTitle(): string {
    if (this.badgeVariant === 'vendedor') return 'Mis notificaciones';
    if (this.notificationParams?.code) return 'Notificaciones del cliente';
    return 'Notificaciones';
  }

  formatDateFromStartAt(startAt?: string): string {
    if (!startAt) return '';
    const datePart = startAt.split(' ')[0];
    return datePart || '';
  }

  /** Normaliza valores como `IN PREPARATION` o `IN_PREPARATION`. */
  normalizeOrderStatus(raw?: string | null): OrderNotificationStatus | null {
    if (!raw?.trim()) return null;
    const n = raw.trim().toUpperCase().replace(/[\s-]+/g, '_');
    if (
      n === 'REJECTED' ||
      n === 'CREATED' ||
      n === 'CONFIRMED' ||
      n === 'IN_PREPARATION' ||
      n === 'IN_TRANSIT' ||
      n === 'DELIVERED'
    ) {
      return n as OrderNotificationStatus;
    }
    return null;
  }

  /** Bloque de timeline solo si hay estado de avance; rechazados no muestran bloque extra. */
  hasOrderStatusBlock(item: NotificationItem): boolean {
    const s = this.normalizeOrderStatus(item.orderStatus);
    return s != null && s !== 'REJECTED';
  }

  /**
   * Cantidad de pasos completados en el timeline (1–5). No aplica a REJECTED.
   * CREATED→1, CONFIRMED→2, IN_PREPARATION→3, IN_TRANSIT→4, DELIVERED→5.
   */
  getOrderTimelineCompletedCount(item: NotificationItem): number {
    const s = this.normalizeOrderStatus(item.orderStatus);
    if (!s || s === 'REJECTED') return 0;
    switch (s) {
      case 'CREATED':
        return 1;
      case 'CONFIRMED':
        return 2;
      case 'IN_PREPARATION':
        return 3;
      case 'IN_TRANSIT':
        return 4;
      case 'DELIVERED':
        return 5;
      default:
        return 0;
    }
  }

  /** Línea entre nodo `lineIndex` y `lineIndex + 1` en verde. */
  isOrderTimelineConnectorDone(item: NotificationItem, lineIndex: number): boolean {
    return this.getOrderTimelineCompletedCount(item) > lineIndex + 1;
  }

  /** Índice del paso actual (0–4), alineado con `orderStatus`. */
  getOrderTimelineCurrentStepIndex(item: NotificationItem): number {
    const n = this.getOrderTimelineCompletedCount(item);
    if (n < 1) return -1;
    return Math.min(n - 1, this.orderTimelineSteps.length - 1);
  }

  /** Pasos pendientes: número en círculo gris. */
  isOrderTimelineFutureStep(item: NotificationItem, stepIndex: number): boolean {
    return stepIndex > this.getOrderTimelineCurrentStepIndex(item);
  }

  /** Negrita solo en el paso actual (último estado alcanzado); el resto usa estilo por defecto. */
  isOrderTimelineStepLabelActive(item: NotificationItem, stepIndex: number): boolean {
    const cur = this.getOrderTimelineCurrentStepIndex(item);
    return cur >= 0 && stepIndex === cur;
  }

  hasNotificationUrl(item: NotificationItem): boolean {
    return !!(item.url?.trim());
  }

  onCardClick(notification: NotificationItem, event: Event): void {
    event.stopPropagation();
    this.markAsReadOnly(notification);
  }

  markAsReadOnly(notification: NotificationItem): void {
    if (!notification.id || notification.read) return;
    this.notificationsService.markNotificationAsNotified(notification.id).subscribe({
      next: () => {
        notification.read = true;
        notification.isNotified = true;
        const unreadCount = this.notifications.filter((n) => !n.read).length;
        this.unreadCountChange.emit(unreadCount);
        this.cdr.detectChanges();
      },
    });
  }

  /**
   * Las URLs del servicio apuntan a flujo cliente. Si la sesión es vendedor (p. ej. con cliente seleccionado),
   * se reemplazan por las rutas equivalentes en /main (mismo criterio que el checkout de pedido realizado).
   */
  private getAdjustedNotificationUrl(url: string): string {
    if (!url?.trim()) {
      return url;
    }
    const user = this.localStorageService.getItem('user');
    if (user?.type !== 'Vendedor') {
      return url;
    }
    return url
      .replace('/tienda/pedido-realizado', '/main/checkout')
      .replace('/cuenta/historial/detalle', '/main/historial-compras/detalle-historial');
  }

  markAsReadAndOpenDetail(notification: NotificationItem): void {
    this._gtmEventService.clickElementEvent({
      cta: 'Ver detalles',
      seccion: 'Notificaciones',
    });
    // Si no tiene ID, solo abrimos la URL en una nueva pestaña
    if (!notification.id) {
      if (notification.url?.trim()) {
        window.open(this.getAdjustedNotificationUrl(notification.url), '_blank', 'noopener');
      }
      return;
    }

    // Marcamos como notificado y luego abrimos la URL
    this.notificationsService.markNotificationAsNotified(notification.id).subscribe({
      next: () => {
        notification.read = true;
        notification.isNotified = true;
        const unreadCount = this.notifications.filter((n) => !n.read).length;
        this.unreadCountChange.emit(unreadCount);
        this.cdr.detectChanges();

        if (notification.url?.trim()) {
          window.open(this.getAdjustedNotificationUrl(notification.url), '_blank', 'noopener');
        }
      },
      error: () => {
        if (notification.url?.trim()) {
          window.open(this.getAdjustedNotificationUrl(notification.url), '_blank', 'noopener');
        }
      },
    });
  }
}
